import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import riskIcon from "../assets/risk_10334106.png";
import alertIcon from "../assets/alert_10263464.png";
import wellnessIcon from "../assets/wellness_18707818.png";

/**
 * Advanced Capabilities Section
 * 
 * Dark blue card with three capability sections:
 * - Disease Risk Prediction
 * - Abnormality Detection
 * - Wellness Insights
 */
const SmartReportVsVizAppBanner = () => {

  const capabilities = [
    {
      title: "Advanced Risk Prediction",
      description: "AI-powered predictive analytics identifying health risks based on test parameters",
      icon: riskIcon,
      iconAlt: "Risk Score",
    },
    {
      title: "Abnormality Detection",
      description: "Instant flagging and highlighting of concerning test results with clinical context",
      icon: alertIcon,
      iconAlt: "Alert",
    },
    {
      title: "Wellness Insights",
      description: "Personalized health recommendations and lifestyle optimization suggestions",
      icon: wellnessIcon,
      iconAlt: "Wellness",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        padding: {
          xs: "42px 16px", // 70% of 60px
          sm: "56px 24px", // 70% of 80px
          md: "70px 48px", // 70% of 100px
        },
        backgroundColor: "transparent",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          background: "linear-gradient(135deg, #022674 0%, #1E4DB7 100%)",
          borderRadius: { xs: "20px", md: "28px" },
          padding: {
            xs: "32px 20px",
            sm: "40px 32px",
            md: "56px 48px",
          },
          boxShadow: "0 16px 64px rgba(2, 38, 116, 0.4)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 50% 0%, rgba(123, 181, 140, 0.08) 0%, transparent 60%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Content Container */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Section Title */}
          <Typography
            sx={{
              fontSize: { xs: "24px", sm: "32px", md: "42px" },
              fontWeight: 700,
              fontFamily: "'Poppins', sans-serif",
              color: "#ffffff",
              textAlign: "center",
              marginBottom: { xs: "32px", md: "48px" },
              letterSpacing: "-0.5px",
              textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            Advanced Capabilities
          </Typography>

          {/* Capabilities Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: "24px", sm: "28px", md: "32px" },
              alignItems: "stretch",
            }}
          >
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ height: "100%", display: "flex" }} // Ensure motion.div takes full height
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    width: "100%",
                    height: "100%",
                    gap: { xs: "12px", md: "16px" },
                    padding: { xs: "24px 16px", md: "32px 24px" },
                    borderRadius: { xs: "16px", md: "20px" },
                    background: "rgba(255, 255, 255, 0.04)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(30, 77, 183, 0.1) 0%, rgba(2, 38, 116, 0.1) 100%)",
                      opacity: 0,
                      transition: "opacity 0.4s ease",
                    },
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transform: "translateY(-8px)",
                      boxShadow: "0 16px 40px rgba(30, 77, 183, 0.25)",
                      "&::before": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Icon Container - Refined size and blue theme */}
                  <Box
                    sx={{
                      width: { xs: "72px", sm: "80px", md: "88px" },
                      height: { xs: "72px", sm: "80px", md: "88px" },
                      borderRadius: "18px",
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)",
                      border: "1.5px solid rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: { xs: "10px", md: "14px" },
                      flexShrink: 0,
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "18px",
                        background: "linear-gradient(135deg, rgba(30, 77, 183, 0.3) 0%, rgba(2, 38, 116, 0.2) 100%)",
                        opacity: 0,
                        transition: "opacity 0.4s ease",
                      },
                      "&:hover": {
                        transform: "scale(1.08) translateY(-2px)",
                        boxShadow: "0 10px 28px rgba(30, 77, 183, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                        borderColor: "rgba(255, 255, 255, 0.35)",
                        "&::after": {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={capability.icon}
                      alt={capability.iconAlt}
                      sx={{
                        width: { xs: "40px", sm: "44px", md: "48px" },
                        height: { xs: "40px", sm: "44px", md: "48px" },
                        objectFit: "contain",
                        filter: "drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3))",
                        position: "relative",
                        zIndex: 1,
                        transition: "transform 0.4s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      fontSize: { xs: "18px", sm: "20px", md: "22px" },
                      fontWeight: 700,
                      fontFamily: "'Poppins', sans-serif",
                      color: "#ffffff",
                      lineHeight: 1.3,
                      marginBottom: { xs: "6px", md: "8px" },
                      flexShrink: 0,
                    }}
                  >
                    {capability.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", sm: "14px", md: "15px" },
                      fontWeight: 400,
                      fontFamily: "'Inter', sans-serif",
                      color: "rgba(255, 255, 255, 0.9)",
                      lineHeight: 1.6,
                      flex: 1,
                      maxWidth: "280px",
                    }}
                  >
                    {capability.description}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SmartReportVsVizAppBanner;
